import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@scio3/ui';
import { Trans, useTranslation } from 'react-i18next';
import { create } from 'zustand';
import {
  VIDEO_WALL_OVERWRITE_KEYS,
  type VideoWallOverwriteAnswer,
  type VideoWallOverwriteMessage,
} from '@/features/screens/utils/video-wall-overwrite';
import { formatVideoWallLayout } from '@/features/assets/utils/video-wall';

/**
 * Global "Replace Video Wall layout?" dialog — render once in App.tsx next to ConfirmDialog.
 * Triggered imperatively via `useVideoWallOverwrite()`. Always the same three outcomes (spec D5):
 * Yes → 'replace', No → 'keep', Cancel / Esc / dismiss → 'cancel' — the server honours the
 * answer per device, so No is offered for every target kind and push type.
 * `useConfirm()` resolves a boolean, which is why this is its own dialog (same look, same
 * promise API, same single mount).
 */

export type VideoWallOverwriteDialogProps = VideoWallOverwriteMessage;

interface VideoWallOverwriteState {
  isOpen: boolean;
  options: VideoWallOverwriteDialogProps | null;
  resolve: ((value: VideoWallOverwriteAnswer) => void) | null;
  open: (
    options: VideoWallOverwriteDialogProps,
    resolve: (value: VideoWallOverwriteAnswer) => void,
  ) => void;
  close: (answer: VideoWallOverwriteAnswer) => void;
}

export const useVideoWallOverwriteStore = create<VideoWallOverwriteState>((set, get) => ({
  isOpen: false,
  options: null,
  resolve: null,

  open: (options, resolve) => set({ isOpen: true, options, resolve }),
  close: (answer) => {
    const { resolve } = get();
    resolve?.(answer);
    set({ isOpen: false, options: null, resolve: null });
  },
}));

/**
 * ```ts
 * const askVideoWallOverwrite = useVideoWallOverwrite();
 * const answer = await askVideoWallOverwrite(videoWallOverwriteMessage(input));
 * if (answer === 'cancel') return;
 * ```
 */
export function useVideoWallOverwrite() {
  const open = useVideoWallOverwriteStore((s) => s.open);

  return (options: VideoWallOverwriteDialogProps): Promise<VideoWallOverwriteAnswer> =>
    new Promise((resolve) => open(options, resolve));
}

export function VideoWallOverwriteDialog() {
  const { t } = useTranslation();
  const { isOpen, options, close } = useVideoWallOverwriteStore();

  if (!options) return null;

  // `incoming` is either a raw layout string or the one `incomingUnknown` key (D4) —
  // only that known key is translated, never arbitrary param values. Raw layouts
  // ("3840x1080+0+0:2") are shown in template-name style ("2x1 - 3840x1080").
  const { current, incoming } = options.params;
  const params = {
    ...options.params,
    ...(current !== undefined && { current: formatVideoWallLayout(current) }),
    incoming:
      incoming === VIDEO_WALL_OVERWRITE_KEYS.incomingUnknown
        ? t(VIDEO_WALL_OVERWRITE_KEYS.incomingUnknown)
        : formatVideoWallLayout(incoming ?? ''),
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && close('cancel')}>
      <AlertDialogContent data-testid="video-wall-overwrite-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t(options.titleKey)}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <p
              className="overflow-hidden [word-break:break-word]"
              data-testid="video-wall-overwrite-body"
            >
              <Trans
                i18nKey={options.bodyKey}
                values={params}
                components={{
                  no: <strong />,
                  layout: <strong className="font-semibold whitespace-nowrap text-foreground" />,
                }}
              />
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="video-wall-overwrite-cancel"
            onClick={() => close('cancel')}
          >
            {t('Cancel')}
          </AlertDialogCancel>
          <Button
            data-testid="video-wall-overwrite-keep"
            variant="outline"
            onClick={() => close('keep')}
          >
            {t(VIDEO_WALL_OVERWRITE_KEYS.no)}
          </Button>
          <AlertDialogAction
            data-testid="video-wall-overwrite-replace"
            onClick={() => close('replace')}
          >
            {t(VIDEO_WALL_OVERWRITE_KEYS.yes)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
